import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Space, Button, Spin, Alert, Typography } from 'antd';
import {
  HomeOutlined, RocketOutlined, PictureOutlined, GiftOutlined,
  AppstoreOutlined, ArrowRightOutlined, CloudSyncOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthService from '../utils/auth';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';
const { Text, Title } = Typography;

export default function ResourceCenter() {
  const navigate = useNavigate();
  const openTab = (path) => navigate(path);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const [products, setProducts] = useState([]);

  useEffect(() => { load(); }, []);
  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/resource-center/overview`, { headers: AuthService.getAuthHeaders() });
      setStats(res.data.stats || {});
      setProducts(res.data.products || []);
    } catch (e) {
      // 静默：页面仍展示资源入口
    } finally {
      setLoading(false);
    }
  };

  // 资源卡片（点击进入对应管理页）
  const RES = [
    { key: 'hotels', label: '在用酒店', unit: '家', icon: <HomeOutlined />, color: '#1677FF', path: '/admin/hotels', tab: '酒店库管理' },
    { key: 'airlines', label: '在用航司', unit: '家', icon: <RocketOutlined />, color: '#00b42a', path: '/admin/airlines', tab: '航司管理' },
    { key: 'attachments', label: '附件（行程等）', unit: '份', icon: <PictureOutlined />, color: '#ff7d00', path: '/admin/notice-attachments', tab: '附件管理' },
    { key: 'addons', label: 'Add-ons', unit: '项', icon: <GiftOutlined />, color: '#eb2f96', path: '/admin/addon-products', tab: 'Add-ons产品' },
  ];

  const columns = [
    { title: '产品', dataIndex: 'name', key: 'name', render: (v) => <Text strong>{v}</Text> },
    { title: '类型', dataIndex: 'categoryLabel', key: 'cat', width: 90,
      render: (v) => <Tag color={v === '出境游' ? 'blue' : v === '入境游' ? 'green' : 'gold'}>{v}</Tag> },
    { title: '来源', dataIndex: 'source', key: 'source', width: 120,
      render: (v) => v === 'passport'
        ? <Tag>手工</Tag>
        : <Tag icon={<CloudSyncOutlined />} color="processing">member 同步</Tag> },
    { title: '出团数', dataIndex: 'tourCount', key: 'tc', width: 90, align: 'center',
      render: (v) => v ? <b style={{ color: '#1677FF' }}>{v}</b> : <span style={{ color: '#bbb' }}>0</span> },
    { title: '游客数', dataIndex: 'touristCount', key: 'pc', width: 90, align: 'center' },
    { title: '含规格', dataIndex: 'specs', key: 'specs',
      render: (specs) => (!specs || !specs.length)
        ? <span style={{ color: '#bbb' }}>—</span>
        : <Space size={4} wrap>{specs.map((s, i) =>
            <Tag key={i} color={s.type === 'package' ? 'blue' : 'gold'} style={{ margin: 0 }}>{s.label}</Tag>)}</Space> },
  ];

  return (
    <div style={{ maxWidth: 1160 }}>
      <Title level={4} style={{ marginBottom: 2 }}>
        <AppstoreOutlined style={{ marginRight: 8 }} />资源中心
      </Title>
      <Text type="secondary" style={{ fontSize: 13 }}>
        拼装出团通知书用的「料」——看你用过哪些<b>酒店 / 航司 / 模板 / 附件</b>，及各产品的出团与规格。
        产品由 member 自动同步，<b>本系统不再手建产品</b>。
      </Text>

      <Spin spinning={loading}>
        <Row gutter={12} style={{ margin: '18px 0 8px' }}>
          {RES.map((r) => (
            <Col xs={12} md={8} lg={Math.floor(24 / RES.length) || 5} key={r.key} style={{ marginBottom: 12 }}>
              <Card size="small" hoverable onClick={() => openTab(r.path, r.tab)} style={{ cursor: 'pointer' }}>
                <Statistic
                  title={<span>{r.icon} {r.label}</span>}
                  value={stats[r.key] ?? 0}
                  suffix={<span style={{ fontSize: 13, color: '#999' }}>{r.unit}</span>}
                  valueStyle={{ color: r.color, fontSize: 26 }}
                />
                <div style={{ marginTop: 6, color: r.color, fontSize: 12 }}>
                  进入管理 <ArrowRightOutlined style={{ fontSize: 10 }} />
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <Card
          size="small"
          title={<span><CloudSyncOutlined style={{ marginRight: 6 }} />产品（团）一览 · 只读</span>}
          style={{ marginTop: 8 }}
          extra={<Text type="secondary" style={{ fontSize: 12 }}>共 {products.length} 个产品</Text>}
        >
          <Alert
            type="info" showIcon style={{ marginBottom: 12, fontSize: 12.5 }}
            message="产品全部由 member 同步生成，运营不再手建/编辑。它只是「多班期 + 多规格」的分组锚点（合并同产品、显示规格都靠它）。"
          />
          <Table
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={products}
            pagination={{ pageSize: 15, hideOnSinglePage: true, showSizeChanger: false }}
          />
        </Card>
      </Spin>
    </div>
  );
}
